import { atomWithStringStorage } from "$root/third-party-integrations/jotai";
import { atom } from "@mdotm/shadcn/legacy/reactivity";

/** The key this token used to be persisted under; see {@link accessToken$}. */
const LEGACY_ACCESS_TOKEN_KEY = "accessToken";

// Builds before MDOT-24702 persisted the bearer token, so every browser that ever ran one still
// holds it. Nothing reads it now, but a credential left at rest forever in users' browsers is
// not something to shrug at — drop it once, on the way past. Removable once no browser can
// still be carrying one (i.e. comfortably after this ships).
window.localStorage.removeItem(LEGACY_ACCESS_TOKEN_KEY);

/**
 * The bearer token every authenticated request carries (see `user/auth-interceptors.ts`).
 *
 * In-memory, and deliberately so. It used to be persisted, back when it *was* the session:
 * the app owned its own tokens and a reload had nothing else to recover them from. Since the
 * Cognito migration the durable session is oidc-client-ts's stored `User` — access token,
 * refresh token and expiry together, under `oidc.user:{authority}:{client_id}` — and this atom
 * is a cache of `user.access_token`, written on `addUserLoaded`, on renew and by
 * `authSessionInit`. Persisting a cache of that only bought one thing: a token present, and
 * trusted, before anything had verified it — which is a staleness hazard, not a feature. The
 * one path that could leave a token with no owner (autologin failing to hydrate, so `logout`
 * never runs) now leaves nothing behind at all.
 *
 * Boot re-derives it: App holds the loading screen until autologin has resolved the OIDC user
 * (renewing it if the access token has expired), so nothing reads this before it is filled.
 */
export const accessToken$ = atom<string | null>(null);

/**
 * Token held for a user who is gated on forced MFA enrollment. While gated we keep
 * `accessToken$` **clear** so no authenticated side-channel (SSE, axios interceptor,
 * queries) activates before enrollment completes; the `/mfa/enroll/*` calls read the
 * token from here instead. Promoted to `accessToken$` by `authSessionInit` once the
 * user resolves as enrolled. See `functional-areas/user/mfa.ts`.
 *
 * In-memory (not persisted): keeping a second bearer token at rest would undercut the
 * point of holding it out of `accessToken$`, and it's re-derived on reload — App's
 * autologin awaits `authSessionInit` (which re-stashes it) before the enrollment
 * screen can mount, so there's no gap to bridge.
 */
export const mfaEnrollmentToken$ = atom<string | null>(null);

/**
 * When set, the app is impersonating this user: the impersonator's own
 * `accessToken$` is preserved and every authenticated request additionally
 * carries an `X-Impersonate-Email` header (see `auth-interceptors`). Persisted so the
 * impersonation survives a page reload, and cleared on logout / new sign-in.
 */
export const impersonateEmail$ = atomWithStringStorage(localStorage, "impersonateEmail");

/**
 * True while the app is moving between identities — impersonation in, impersonation out.
 *
 * `PlatformRouter` renders a loader in place of the routed page for as long as it is set, so every
 * page is unmounted for the crossing, and with it every grid that persists its own column layout.
 * That is what keeps a layout attributed to the user who arranged it: a user-scoped preference write
 * is attributed to whichever identity is current when its request is **sent**, and a grid left
 * mounted across the crossing re-renders against the arriving identity's store and persists the
 * outgoing identity's layout under it.
 *
 * Unmounting is also what makes those writes settleable. A column-state debounce flushes on unmount
 * (`usePersistColState`), so the value reaches `userStorage`'s queue while the outgoing identity's
 * header is still on, where `settlePendingWrites` drains it — see `user/pending-writes.ts`.
 *
 * In-memory: a reload is a fresh boot, which is never mid-crossing.
 */
export const identityTransition$ = atom(false);
